import { compareCalendarItems, type TodayCalendar, type TodayCalendarItem } from '@fos/shared/contracts/today';
import { addDaysLocal } from '@fos/shared/time';
import { formatClock, plural } from '../../lib/format';
import type { Tone } from '../../ui/Status';

/**
 * Ekran Dziś: widoki Dzień / Tydzień / Miesiąc (DECISIONS 2026-10-09 „Dziś: Dzień / Tydzień / Miesiąc”) — każdy ma
 * inny cel: Dzień „co teraz”, Tydzień „jak rozłożona praca”, Miesiąc „z góry”. Tu tylko logika bez Reacta (zakresy
 * dat, siatka miesiąca, kolor dnia, liczby, zapamiętany widok).
 */

export const TODAY_VIEWS = ['day', 'week', 'month'] as const;
export type TodayView = (typeof TODAY_VIEWS)[number];
export const TODAY_VIEW_LABELS: Record<TodayView, string> = { day: 'Dzień', week: 'Tydzień', month: 'Miesiąc' };

/** Wybór widoku pamiętany per osoba w tej przeglądarce. */
export function viewStorageKey(userId: string): string {
  return `fos.today.view.${userId}`;
}

export function readStoredView(userId: string): TodayView {
  try {
    const v = localStorage.getItem(viewStorageKey(userId));
    return (TODAY_VIEWS as readonly string[]).includes(v ?? '') ? (v as TodayView) : 'day';
  } catch {
    return 'day';
  }
}

export function storeView(userId: string, view: TodayView): void {
  try {
    localStorage.setItem(viewStorageKey(userId), view);
  } catch {
    // tryb prywatny: bez zapamiętywania
  }
}

// ── Daty ────────────────────────────────────────────────────────────────────

const MONTHS = [
  'Styczeń',
  'Luty',
  'Marzec',
  'Kwiecień',
  'Maj',
  'Czerwiec',
  'Lipiec',
  'Sierpień',
  'Wrzesień',
  'Październik',
  'Listopad',
  'Grudzień',
] as const;
const MONTHS_GENITIVE = [
  'stycznia',
  'lutego',
  'marca',
  'kwietnia',
  'maja',
  'czerwca',
  'lipca',
  'sierpnia',
  'września',
  'października',
  'listopada',
  'grudnia',
] as const;
/** Litery dni tygodnia od poniedziałku (siatka miesiąca). */
export const WEEKDAY_LETTERS = ['P', 'W', 'Ś', 'C', 'P', 'S', 'N'] as const;
/** Skróty dni tygodnia od poniedziałku (kolumny tygodnia). */
export const WEEKDAY_SHORT = ['pn', 'wt', 'śr', 'czw', 'pt', 'sb', 'nd'] as const;

function parts(date: string): { y: number; m: number; d: number } {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return { y, m, d };
}

/** Dzień tygodnia 0 = poniedziałek … 6 = niedziela. */
export function weekdayIndex(date: string): number {
  const { y, m, d } = parts(date);
  return (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
}

export function dayOfMonth(date: string): number {
  return parts(date).d;
}

export interface DateRange {
  from: string;
  to: string;
}

/** Tydzień (pn–nd) zawierający dzień. */
export function weekRange(date: string): DateRange {
  const from = addDaysLocal(date, -weekdayIndex(date));
  return { from, to: addDaysLocal(from, 6) };
}

/** Miesiąc zawierający dzień (1.–ostatni). */
export function monthRange(date: string): DateRange {
  const { y, m } = parts(date);
  const from = `${y}-${String(m).padStart(2, '0')}-01`;
  const next = m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, '0')}-01`;
  return { from, to: addDaysLocal(next, -1) };
}

/** Przesunięcie kotwicy widoku o tydzień / miesiąc (miesiąc: zawsze 1. dzień, bez problemu 31 → luty). */
export function shiftAnchor(view: 'week' | 'month', anchor: string, step: -1 | 1): string {
  if (view === 'week') return addDaysLocal(anchor, 7 * step);
  const { y, m } = parts(anchor);
  const total = y * 12 + (m - 1) + step;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}-01`;
}

export function daysOf(range: DateRange): string[] {
  const out: string[] = [];
  for (let d = range.from; d <= range.to; d = addDaysLocal(d, 1)) out.push(d);
  return out;
}

/** Siatka miesiąca: puste pola przed 1. dniem (od poniedziałku), potem kolejne dni. */
export function monthCells(range: DateRange): (string | null)[] {
  return [...Array.from({ length: weekdayIndex(range.from) }, () => null), ...daysOf(range)];
}

/** „6–12 października”, „29 września – 5 października”, „29 grudnia 2026 – 4 stycznia 2027”. */
export function weekLabel(range: DateRange): string {
  const a = parts(range.from);
  const b = parts(range.to);
  if (a.y !== b.y) {
    return `${a.d} ${MONTHS_GENITIVE[a.m - 1]} ${a.y} – ${b.d} ${MONTHS_GENITIVE[b.m - 1]} ${b.y}`;
  }
  if (a.m !== b.m) return `${a.d} ${MONTHS_GENITIVE[a.m - 1]} – ${b.d} ${MONTHS_GENITIVE[b.m - 1]}`;
  return `${a.d}–${b.d} ${MONTHS_GENITIVE[b.m - 1]}`;
}

/** „Październik 2026” */
export function monthLabel(range: DateRange): string {
  const { y, m } = parts(range.from);
  return `${MONTHS[m - 1]} ${y}`;
}

// ── Pozycje ─────────────────────────────────────────────────────────────────

/** Pozycje dzień po dniu, w kolejności `compareCalendarItems` (najważniejsze pierwsze — kafelki tygodnia). */
export function itemsByDay(items: readonly TodayCalendarItem[]): Map<string, TodayCalendarItem[]> {
  const map = new Map<string, TodayCalendarItem[]>();
  for (const item of [...items].sort(compareCalendarItems)) {
    const list = map.get(item.date);
    if (list) list.push(item);
    else map.set(item.date, [item]);
  }
  return map;
}

/**
 * Kolor dnia w siatce miesiąca: najważniejsza rzecz tego dnia — po terminie (czerwony) > odnowienie / płatność
 * (bursztyn) > spotkanie (niebieski) > zadanie albo termin projektu (szary). Dzień bez niczego: null.
 */
export function dayTone(items: readonly TodayCalendarItem[] | undefined): Tone | null {
  if (!items || items.length === 0) return null;
  if (items.some((i) => i.overdue)) return 'danger';
  if (items.some((i) => i.kind === 'renewal')) return 'warning';
  if (items.some((i) => i.kind === 'meeting')) return 'accent';
  return 'neutral';
}

export interface CalendarCounts {
  /** zadania (bez zaległych) */
  tasks: number;
  meetings: number;
  /** odnowienia i płatności (bez zaległych) */
  renewals: number;
  /** zaległe zadania i odnowienia */
  overdue: number;
  projectDeadlines: number;
}

export function countItems(items: readonly TodayCalendarItem[]): CalendarCounts {
  const c: CalendarCounts = { tasks: 0, meetings: 0, renewals: 0, overdue: 0, projectDeadlines: 0 };
  for (const i of items) {
    if (i.overdue) c.overdue += 1;
    else if (i.kind === 'task') c.tasks += 1;
    else if (i.kind === 'meeting') c.meetings += 1;
    else if (i.kind === 'renewal') c.renewals += 1;
    else c.projectDeadlines += 1;
  }
  return c;
}

/** „8:40 h” */
export function formatHours(seconds: number): string {
  return `${formatClock(seconds)} h`;
}

export function totalSeconds(days: TodayCalendar['days']): number | null {
  return days ? days.reduce((s, d) => s + d.seconds, 0) : null;
}

/** Jedna linijka tygodnia: „5 zadań · 3 spotkania · 1 odnowienie · 1 po terminie · 8:40 h” (zera pominięte). */
export function summaryLine(c: CalendarCounts, seconds: number | null): string {
  const parts: string[] = [];
  if (c.tasks) parts.push(`${c.tasks} ${plural(c.tasks, ['zadanie', 'zadania', 'zadań'])}`);
  if (c.meetings) parts.push(`${c.meetings} ${plural(c.meetings, ['spotkanie', 'spotkania', 'spotkań'])}`);
  if (c.renewals) parts.push(`${c.renewals} ${plural(c.renewals, ['odnowienie', 'odnowienia', 'odnowień'])}`);
  if (c.projectDeadlines) {
    parts.push(
      `${c.projectDeadlines} ${plural(c.projectDeadlines, ['termin projektu', 'terminy projektów', 'terminów projektów'])}`,
    );
  }
  if (c.overdue) parts.push(`${c.overdue} po terminie`);
  if (seconds) parts.push(formatHours(seconds));
  return parts.length > 0 ? parts.join(' · ') : 'Nic zaplanowanego';
}

/** Godzina spotkania bez zera wiodącego: „09:30” → „9:30”. */
export function shortTime(time: string): string {
  const [h, m] = time.split(':');
  return `${Number(h)}:${m}`;
}

/** Podpis rodzaju dla czytników ekranu i dymków. */
export function kindLabel(item: TodayCalendarItem): string {
  if (item.overdue) return 'Po terminie';
  switch (item.kind) {
    case 'meeting':
      return 'Spotkanie';
    case 'renewal':
      return 'Odnowienie';
    case 'project_deadline':
      return 'Termin projektu';
    default:
      return 'Zadanie';
  }
}
